import { createFileRoute, redirect } from '@tanstack/react-router';

import { medplum } from '@/config/medplum';
import { APP_PERMISSIONS, getUserPermissions, hasPermission } from '@/lib/auth/permissions';
import { sanitizeInternalPath } from '@/lib/auth/redirect';

type SearchParams = {
  error?: string;
  redirect?: string;
};

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    error: typeof search.error === 'string' ? search.error : undefined,
    redirect: sanitizeInternalPath(search.redirect),
  }),
  beforeLoad: async ({ search }) => {
    const activeLogin = medplum.getActiveLogin();

    if (activeLogin) {
      const { permissions } = await getUserPermissions();
      // Empty permissions with an activeLogin means the profile couldn't be loaded
      // (expired/invalid token). Stay on the login page — onUnauthenticated will
      // clear the stale session.
      if (permissions.length === 0) return;
      if (search.redirect) {
        throw redirect({ to: search.redirect as string, replace: true });
      }
      if (
        hasPermission(permissions, APP_PERMISSIONS.PATIENT_CARE) ||
        hasPermission(permissions, APP_PERMISSIONS.CARE_ADMIN)
      ) {
        throw redirect({ to: '/patients', replace: true });
      }
      if (hasPermission(permissions, APP_PERMISSIONS.PATIENT_MEDREC)) {
        throw redirect({ to: '/patients', search: { tab: 'medrec' }, replace: true });
      }
      // A facilities_admin without the admin dashboard has nothing to do on the
      // dashboard — send them to the only surface they can act on.
      if (
        hasPermission(permissions, APP_PERMISSIONS.FACILITIES_ADMIN) &&
        !hasPermission(permissions, APP_PERMISSIONS.ADMIN_DASHBOARD)
      ) {
        throw redirect({ to: '/entities', replace: true });
      }
      // Enrollment roles can't use /patients — send them to their referrals
      // workspace instead.
      if (
        hasPermission(permissions, APP_PERMISSIONS.ENROLLMENT_ADMIN) ||
        hasPermission(permissions, APP_PERMISSIONS.PATIENT_ENROLLMENT)
      ) {
        throw redirect({ to: '/referrals', replace: true });
      }
      throw redirect({ to: '/patients', replace: true });
    }
  },
});
